import React, { useState } from "react";
import { Shield, AlertTriangle, Phone, Send, CheckCircle2, PhoneOff, MailWarning, Lock, Loader2 } from "lucide-react";
import { SITE } from "../lib/site";
import { api } from "../lib/api";
import { toast } from "sonner";

const RED_FLAGS = [
  { icon: PhoneOff, text: "“We’re from Microsoft / Telstra / the ATO — let us into your computer.”" },
  { icon: MailWarning, text: "An email or text says click this link right now or your account closes." },
  { icon: AlertTriangle, text: "Someone urgently asks for gift cards, bank transfers, or remote access." },
  { icon: Lock, text: "A pop-up locks your screen with a phone number to call. Don’t call it." },
];

export default function ScamProtection() {
  const [form, setForm] = useState({ name: "", phone: "", description: "", urgency: "normal" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.description.trim()) {
      toast.error("Please fill in your name, phone, and what happened.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/scam-checks", form);
      setDone(true);
      toast.success("Got it — I’ll call you back as soon as I can.");
      setForm({ name: "", phone: "", description: "", urgency: "normal" });
    } catch (err) {
      toast.error("Couldn’t send right now. Please call me on " + SITE.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="scam-help"
      data-testid="scam-section"
      className="py-20 md:py-24 bg-[#FEF3F2] border-y border-[#F1D9D6]"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-[#F1D9D6] rounded-full px-4 py-2 text-sm md:text-base font-semibold text-[#7F1D1D]">
              <Shield className="w-4 h-4" strokeWidth={2.5} />
              <span>Scam protection · 25+ years cyber security</span>
            </div>
            <h2 className="mt-4 font-[Work_Sans] text-3xl md:text-4xl lg:text-5xl font-bold text-[#7F1D1D]">
              Think it might be a scam? <br />Pause. Take a beat. Call me.
            </h2>
            <p className="mt-5 text-lg md:text-xl text-[#5A1717] leading-relaxed">
              Scammers are clever and they target seniors on purpose. There’s no
              shame in checking — that’s exactly what I’m here for. If something
              feels off, <strong>don’t click, don’t pay, don’t give access</strong>.
              Hang up and ring me.
            </p>

            <a
              href={`tel:${SITE.phoneTel}`}
              data-testid="scam-call-button"
              className="mt-7 inline-flex items-center gap-3 bg-[#7F1D1D] hover:bg-[#5A1717] text-white font-bold rounded-xl px-7 py-5 text-xl shadow-sm min-h-[60px]"
            >
              <Phone className="w-6 h-6" strokeWidth={2.5} />
              Scam Help Line — {SITE.phone}
            </a>

            <div className="mt-10">
              <h3 className="font-[Work_Sans] text-2xl font-semibold text-[#0F2E52]">
                Common warning signs
              </h3>
              <ul className="mt-5 space-y-3">
                {RED_FLAGS.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <li
                      key={i}
                      data-testid={`red-flag-${i}`}
                      className="flex items-start gap-4 bg-white border border-[#F1D9D6] rounded-xl p-4 md:p-5"
                    >
                      <div className="w-11 h-11 rounded-lg bg-[#FEF3F2] grid place-items-center shrink-0">
                        <Icon className="w-6 h-6 text-[#7F1D1D]" strokeWidth={2.2} />
                      </div>
                      <p className="text-base md:text-lg text-[#1E3A5F] leading-relaxed">
                        {f.text}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="bg-white border border-[#F1D9D6] rounded-2xl p-6 md:p-8 shadow-sm">
            {done ? (
              <div className="text-center py-8" data-testid="scam-form-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#0F2E52]/15 grid place-items-center">
                  <CheckCircle2 className="w-9 h-9 text-[#0F2E52]" strokeWidth={2.5} />
                </div>
                <h3 className="mt-5 font-[Work_Sans] text-2xl font-bold text-[#0F2E52]">
                  Thanks — I’ll call you back.
                </h3>
                <p className="mt-3 text-lg text-[#1E3A5F]">
                  In the meantime, please don’t click any links or share any codes.
                  If it’s urgent, ring me on <a className="link-underline font-semibold text-[#0F2E52]" href={`tel:${SITE.phoneTel}`}>{SITE.phone}</a>.
                </p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  data-testid="scam-form-reset"
                  className="mt-6 inline-flex items-center justify-center bg-white border-2 border-[#0F2E52] text-[#0F2E52] font-bold rounded-xl px-6 py-3 hover:bg-[#F0F7E8]"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="scam-form" className="space-y-5">
                <div>
                  <h3 className="font-[Work_Sans] text-2xl md:text-3xl font-bold text-[#0F2E52]">
                    “Is this a scam?” — Ask me
                  </h3>
                  <p className="mt-2 text-base md:text-lg text-[#1E3A5F]">
                    Tell me what happened. I’ll get back to you and help you check.
                    It’s free to ask.
                  </p>
                </div>

                <div>
                  <label htmlFor="scam-name" className="block text-base font-semibold text-[#0F2E52] mb-2">Your name</label>
                  <input
                    id="scam-name"
                    data-testid="scam-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Margaret"
                    className="w-full text-lg px-4 py-4 rounded-xl border-2 border-[#DCE4EC] focus:border-[#0F2E52] outline-none bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="scam-phone" className="block text-base font-semibold text-[#0F2E52] mb-2">Phone number</label>
                  <input
                    id="scam-phone"
                    data-testid="scam-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="A number I can call you on"
                    className="w-full text-lg px-4 py-4 rounded-xl border-2 border-[#DCE4EC] focus:border-[#0F2E52] outline-none bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="scam-desc" className="block text-base font-semibold text-[#0F2E52] mb-2">
                    What happened?
                  </label>
                  <textarea
                    id="scam-desc"
                    data-testid="scam-input-description"
                    value={form.description}
                    onChange={update("description")}
                    rows={5}
                    placeholder="e.g. A man rang saying he was from the bank and asked for my code…"
                    className="w-full text-lg px-4 py-4 rounded-xl border-2 border-[#DCE4EC] focus:border-[#0F2E52] outline-none bg-white resize-y"
                  />
                </div>

                <div>
                  <label htmlFor="scam-urgency" className="block text-base font-semibold text-[#0F2E52] mb-2">How urgent?</label>
                  <select
                    id="scam-urgency"
                    data-testid="scam-input-urgency"
                    value={form.urgency}
                    onChange={update("urgency")}
                    className="w-full text-lg px-4 py-4 rounded-xl border-2 border-[#DCE4EC] focus:border-[#0F2E52] outline-none bg-white"
                  >
                    <option value="normal">It just happened, I’m not sure</option>
                    <option value="urgent">Urgent — I may have lost money or access</option>
                    <option value="check">Just checking before I do anything</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="scam-form-submit"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#7F1D1D] hover:bg-[#5A1717] disabled:opacity-60 text-white font-bold rounded-xl px-6 py-5 text-xl min-h-[60px]"
                >
                  {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" strokeWidth={2.5} />}
                  {submitting ? "Sending..." : "Send to The Patient Tech Bloke"}
                </button>

                <p className="text-sm text-[#475569] text-center">
                  Free, no obligation. Your details stay private.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
